import {Directive, Input} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, Validators} from '@angular/forms';
import {AsyncUniqueValidator} from '@application/validators/async-unique-validator';
import {FixedShape} from '@domain/drawing-library/fixed-shape.enum';
import {Drawing} from '@domain/production-schedule/drawing';
import {DrawingConfigurationWithColorAndYarnSet} from '@domain/production-schedule/drawing-configuration-with-color-and-yarn-set';
import {DrawingImage} from '@domain/production-schedule/drawing-image';
import {Subscription as ProfileSubscription} from '@domain/profile/subscription';
import {Drawings} from '@infrastructure/http/drawing/drawings';
import {ListDrawingConfiguration} from '@presentation/components/drawing-list/list-drawing-configuration';
import {OverviewListArticleForOrderLine} from '@presentation/pages/texfab/orderbook/overview/overview-list-article-for-order-line';
import {AssertionUtils, BaseComponent, canShowErrorForErrorCodeAndFormControlForFormGroup, convertToCommercialUnitCentimeter, TranslateService, Unit} from '@vdw/angular-component-library';
import {get, has, isEqual, isNil, reverse, slice} from 'lodash-es';
import {ConfigurationForm, DrawingForm} from './drawing-form';

@Directive()
export abstract class BaseDrawingFormComponent extends BaseComponent {
  @Input() public drawing: Drawing;
  @Input() public drawingShapes: string[];
  @Input() public productionScheduleDrawing: OverviewListArticleForOrderLine | ListDrawingConfiguration;
  public drawingForm: DrawingForm;

  protected drawingsService: Drawings;
  protected currentSubscription: ProfileSubscription;

  protected constructor(
    drawingsService: Drawings,
    protected formBuilder: FormBuilder,
    private readonly translate: TranslateService
  ) {
    super();

    this.drawingsService = drawingsService;
  }

  public getDrawingImageData(): string {
    const drawingImage: DrawingImage = this.drawingForm.getRawValue().drawingImage;

    return get(drawingImage, 'data', null);
  }

  public hasDrawingConfigurations(): boolean {
    return this.drawingForm.value.configurations?.length > 0;
  }

  public getDrawingImage(): DrawingImage {
    return this.drawingForm.getRawValue().drawingImage;
  }

  public getDrawingConfigurationForms(): AbstractControl[] {
    return AssertionUtils.isNullOrUndefined(this.drawingForm.controls.configurations) ? [] : reverse(slice(this.drawingForm.controls.configurations.controls));
  }

  public deleteConfiguration(configurationIndex: number): void {
    this.drawingForm.controls.configurations.removeAt(this.getAmountOfDrawingConfigurations() - configurationIndex - 1);
    if (this.getAmountOfDrawingConfigurations() === 0) {
      this.drawingForm.controls.nrColumnsForWeftSelection?.enable();
    }
  }

  public canShowErrorForFormControl(errorCode: string, formControlName: string): boolean {
    return canShowErrorForErrorCodeAndFormControlForFormGroup(errorCode, formControlName, this.drawingForm);
  }

  public getConfigurationIndexForView(configurationIndex: number): number {
    return this.getAmountOfDrawingConfigurations() - configurationIndex;
  }

  public getExistingQualitiesAndColorSetsOfTheOtherDrawingConfigurations(configurationIndex: number): {[qualityId: string]: number[]} {
    const indexInConfigurationsFormArray: number = this.getAmountOfDrawingConfigurations() - configurationIndex - 1;
    const existingQualitiesAndColorSets: {[qualityId: string]: number[]} = {};

    this.drawingForm.controls.configurations.controls.forEach((configurationForm: ConfigurationForm, index: number) => {
      const configurationQuality = configurationForm.value.quality;
      const configurationColorSet = configurationForm.value.colorSet;
      if (!isEqual(indexInConfigurationsFormArray, index) && !isNil(configurationQuality) && !isNil(configurationColorSet)) {
        if (has(existingQualitiesAndColorSets, configurationQuality.id)) {
          existingQualitiesAndColorSets[configurationQuality.id].push(configurationColorSet.id);
        } else {
          existingQualitiesAndColorSets[configurationQuality.id] = [configurationColorSet.id];
        }
      }
    });

    return existingQualitiesAndColorSets;
  }

  public getShape(drawingShape: string): string {
    return FixedShape.getTranslationKeyForShape(drawingShape, this.translate);
  }

  protected setFormFields(): void {
    this.drawingForm = this.formBuilder.group({
      name: this.formBuilder.control(null, Validators.required),
      drawingImage: this.formBuilder.control(null, Validators.required),
      shape: this.formBuilder.control(null, Validators.required)
    });
  }

  protected setFormFieldsForBmpDrawing(): void {
    this.drawingForm.addControl('code', this.formBuilder.control(null));
    this.drawingForm.addControl('heightInPx', this.formBuilder.control({value: null, disabled: true}, Validators.required));
    this.drawingForm.addControl('widthInPx', this.formBuilder.control({value: null, disabled: true}, Validators.required));
    this.drawingForm.addControl('configurations', new FormArray([]));
    this.drawingForm.addControl('threadByThread', this.formBuilder.control(false));
    this.drawingForm.addControl('nrColumnsForWeftSelection', this.formBuilder.control(null));
  }

  protected setValuesForFormFields(): void {
    this.setAsyncValidatorForDrawingNameControl(!isNil(this.drawing.id) ? this.drawing.name : null);

    this.drawingForm.controls.name.setValue(this.drawing.name);
    this.drawingForm.controls.drawingImage.setValue(this.drawing.image);
    this.drawingForm.controls.shape.setValue(this.drawing.shape);
  }

  protected setValuesForBmpDrawingFormFields(disableAllFields?: boolean): void {
    this.drawingForm.controls.code.setValue(this.drawing.code);
    this.drawingForm.controls.heightInPx.setValue(this.drawing.dimensionsInPixels.heightInPx);
    this.drawingForm.controls.widthInPx.setValue(this.drawing.dimensionsInPixels.widthInPx);
    this.drawingForm.controls.threadByThread.setValue(this.drawing.threadByThread);
    this.drawingForm.controls.nrColumnsForWeftSelection.setValue(this.drawing.nrColumnsForWeftSelection);

    this.deleteAllConfigurations();

    if (this.drawing.configurations.length > 0) {
      this.drawingForm.controls.threadByThread.disable();
    } else {
      this.drawingForm.controls.threadByThread.enable();
    }

    this.drawing.configurations.forEach((configuration: DrawingConfigurationWithColorAndYarnSet) => {
      this.drawingForm.controls.configurations.push(
        this.formBuilder.group({
          quality: this.formBuilder.control(configuration.quality),
          colorSet: this.formBuilder.control(configuration.colorSet),
          yarnSet: this.formBuilder.control(configuration.yarnSet),
          mainColorsCreelPosition: this.formBuilder.control(configuration.mainColorsCreelPosition),
          borderColorsCreelPosition: this.formBuilder.control(configuration.borderColorsCreelPosition),
          image: this.formBuilder.control({id: this.drawing.image.id, data: configuration.image.data} as DrawingImage, Validators.required),
          heightInCm: this.formBuilder.control(
            convertToCommercialUnitCentimeter({
              unit: Unit.MILLIMETER,
              value: configuration.commercialDimensionsInMm.heightInMM
            }),
            Validators.min(0)
          ),
          widthInCm: this.formBuilder.control(
            convertToCommercialUnitCentimeter({
              unit: Unit.MILLIMETER,
              value: configuration.commercialDimensionsInMm.widthInMM
            }),
            Validators.min(0)
          )
        }) as ConfigurationForm
      );
    });

    if (disableAllFields) {
      this.drawingForm.disable();
    }
  }

  protected setAsyncValidatorForDrawingNameControl(originalName: string = null): void {
    this.drawingForm.controls.name.setAsyncValidators([AsyncUniqueValidator.createValidator(this.drawingsService, originalName)]);
  }

  private deleteAllConfigurations(): void {
    while (this.drawingForm.controls.configurations.length > 0) {
      this.deleteConfiguration(0);
    }
    this.drawingForm.controls.nrColumnsForWeftSelection?.enable();
  }

  private getAmountOfDrawingConfigurations(): number {
    return this.drawingForm.controls.configurations.controls.length;
  }
}
